import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reconcileInvoice } from 'Services/invoices';
import { reconcileTransaction } from 'Services/transactions';
import { trackError } from 'Utils/errorMonitoring';
import { INVOICES_RECONCILED_STATUS } from 'Utils/constants';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { useEventDispatcher } from 'Components/Organisms/EventDispatcherContext';
import { RECONCILE_ITEM } from 'Utils/eventTypes';

const { RECONCILED, NOT_RECONCILED } = INVOICES_RECONCILED_STATUS;

async function snapshotAndCancel(queryClient, queryKey) {
    await queryClient.cancelQueries({ queryKey });
    return queryClient.getQueriesData({ queryKey });
}

function restoreSnapshot(queryClient, snapshot) {
    snapshot.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
    });
}

function updateInfiniteQueryItems(data, itemId, updater) {
    if (!data?.pages) return data;
    return {
        ...data,
        pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((item) => (item.id === itemId ? updater(item) : item)),
        })),
    };
}

function removeFromInfiniteQuery(data, itemId) {
    if (!data?.pages) return data;
    return {
        ...data,
        pages: data.pages.map((page) => {
            const hasItem = page.items.some((item) => item.id === itemId);
            return {
                ...page,
                count: hasItem ? page.count - 1 : page.count,
                items: hasItem ? page.items.filter((item) => item.id !== itemId) : page.items,
            };
        }),
    };
}

function optimisticUpdateEntity(queryClient, queryKeyPrefix, entityId, updater, removeFromLists = false) {
    const allQueries = queryClient.getQueriesData({ queryKey: queryKeyPrefix });
    allQueries.forEach(([key, data]) => {
        if (!data) return;

        if (data.id === entityId) {
            queryClient.setQueryData(key, updater(data));
            return;
        }

        if (data.pages) {
            queryClient.setQueryData(
                key,
                removeFromLists
                    ? removeFromInfiniteQuery(data, entityId)
                    : updateInfiniteQueryItems(data, entityId, updater),
            );
        }
    });
}

export default function useReconciliationMutation() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation('reconciliation');
    const eventDispatcher = useEventDispatcher();

    const invoiceMutation = useMutation({
        mutationFn: ({
            invoiceId,
            transactionsIds,
            forceReconciliationStatus,
            forceTransactionsReconciliationStatus,
        }) =>
            reconcileInvoice(invoiceId, {
                transactionsIds,
                ...(forceReconciliationStatus !== undefined && { forceReconciliationStatus }),
                ...(forceTransactionsReconciliationStatus !== undefined && { forceTransactionsReconciliationStatus }),
            }),
        onMutate: async ({ invoiceId, transactionsIds, forceReconciliationStatus }) => {
            const changesReconciliationStatus = forceReconciliationStatus !== undefined;

            const [invoicesSnapshot, transactionsSnapshot] = await Promise.all([
                snapshotAndCancel(queryClient, ['invoices']),
                snapshotAndCancel(queryClient, ['transactions']),
            ]);

            optimisticUpdateEntity(
                queryClient,
                ['invoices'],
                invoiceId,
                (invoice) => ({
                    ...invoice,
                    transactions_associated: transactionsIds,
                    ...(forceReconciliationStatus === true && { reconciled_status: RECONCILED }),
                    ...(forceReconciliationStatus === false && { reconciled_status: NOT_RECONCILED }),
                }),
                changesReconciliationStatus,
            );

            if (forceReconciliationStatus) {
                transactionsIds.forEach((transactionId) => {
                    optimisticUpdateEntity(queryClient, ['transactions'], transactionId, (transaction) => ({
                        ...transaction,
                        is_reconciled: true,
                        invoices_ids: [...new Set([...(transaction.invoices_ids || []), invoiceId])],
                    }));
                });
            }

            return { invoicesSnapshot, transactionsSnapshot };
        },
        onSuccess: (_data, { invoiceId, forceReconciliationStatus }) => {
            if (forceReconciliationStatus) {
                enqueueSnackbar(t('reconciliation-success'), { variant: 'success', key: 'reconciliation-success' });
                eventDispatcher.dispatch(RECONCILE_ITEM, { reference: 'invoices', id: invoiceId });
            }
        },
        onError: (error, _body, context) => {
            restoreSnapshot(queryClient, context.invoicesSnapshot);
            restoreSnapshot(queryClient, context.transactionsSnapshot);
            trackError(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
        },
    });

    const transactionMutation = useMutation({
        mutationFn: ({ transactionId, invoicesIds, forceReconciliationStatus, forceInvoicesReconciliationStatus }) =>
            reconcileTransaction(transactionId, {
                invoicesIds,
                ...(forceReconciliationStatus !== undefined && { forceReconciliationStatus }),
                ...(forceInvoicesReconciliationStatus !== undefined && { forceInvoicesReconciliationStatus }),
            }),
        onMutate: async ({ transactionId, invoicesIds, forceReconciliationStatus }) => {
            const changesReconciliationStatus = forceReconciliationStatus !== undefined;

            const [invoicesSnapshot, transactionsSnapshot] = await Promise.all([
                snapshotAndCancel(queryClient, ['invoices']),
                snapshotAndCancel(queryClient, ['transactions']),
            ]);

            optimisticUpdateEntity(
                queryClient,
                ['transactions'],
                transactionId,
                (transaction) => ({
                    ...transaction,
                    invoices_ids: invoicesIds,
                    ...(forceReconciliationStatus !== undefined && { is_reconciled: forceReconciliationStatus }),
                }),
                changesReconciliationStatus,
            );

            if (forceReconciliationStatus) {
                invoicesIds.forEach((invoiceId) => {
                    optimisticUpdateEntity(queryClient, ['invoices'], invoiceId, (invoice) => ({
                        ...invoice,
                        reconciled_status: RECONCILED,
                        transactions_associated: [
                            ...new Set([...(invoice.transactions_associated || []), transactionId]),
                        ],
                    }));
                });
            }

            return { invoicesSnapshot, transactionsSnapshot };
        },
        onSuccess: (_data, { transactionId, forceReconciliationStatus }) => {
            if (forceReconciliationStatus) {
                enqueueSnackbar(t('reconciliation-success'), { variant: 'success', key: 'reconciliation-success' });
                eventDispatcher.dispatch(RECONCILE_ITEM, { reference: 'transactions', id: transactionId });
            }
        },
        onError: (error, _body, context) => {
            restoreSnapshot(queryClient, context.invoicesSnapshot);
            restoreSnapshot(queryClient, context.transactionsSnapshot);
            trackError(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
        },
    });

    const reconcileInvoiceWithTransactions = useCallback(
        (invoiceId, transactionsIds, options = {}) => {
            invoiceMutation.mutate({ invoiceId, transactionsIds, ...options });
            return true;
        },
        [invoiceMutation.mutate],
    );

    const reconcileTransactionWithInvoices = useCallback(
        (transactionId, invoicesIds, options = {}) => {
            transactionMutation.mutate({ transactionId, invoicesIds, ...options });
            return true;
        },
        [transactionMutation.mutate],
    );

    const markInvoiceAsReconciled = useCallback(
        (invoiceId, transactionsIds, options = {}) =>
            reconcileInvoiceWithTransactions(invoiceId, transactionsIds, {
                forceReconciliationStatus: true,
                ...options,
            }),
        [reconcileInvoiceWithTransactions],
    );

    const markInvoiceAsUnreconciled = useCallback(
        (invoiceId, transactionsIds) =>
            reconcileInvoiceWithTransactions(invoiceId, transactionsIds, {
                forceReconciliationStatus: false,
                forceTransactionsReconciliationStatus: false,
            }),
        [reconcileInvoiceWithTransactions],
    );

    const saveInvoiceAssociations = useCallback(
        (invoiceId, transactionsIds) => reconcileInvoiceWithTransactions(invoiceId, transactionsIds),
        [reconcileInvoiceWithTransactions],
    );

    const removeTransactionFromInvoice = useCallback(
        (invoiceId, transactionsIds) => reconcileInvoiceWithTransactions(invoiceId, transactionsIds, { silent: true }),
        [reconcileInvoiceWithTransactions],
    );

    const markTransactionAsReconciled = useCallback(
        (transactionId, invoicesIds, options = {}) =>
            reconcileTransactionWithInvoices(transactionId, invoicesIds, {
                forceReconciliationStatus: true,
                ...options,
            }),
        [reconcileTransactionWithInvoices],
    );

    const markTransactionAsUnreconciled = useCallback(
        (transactionId, invoicesIds) =>
            reconcileTransactionWithInvoices(transactionId, invoicesIds, {
                forceReconciliationStatus: false,
                forceInvoicesReconciliationStatus: false,
            }),
        [reconcileTransactionWithInvoices],
    );

    const saveTransactionAssociations = useCallback(
        (transactionId, invoicesIds) => reconcileTransactionWithInvoices(transactionId, invoicesIds),
        [reconcileTransactionWithInvoices],
    );

    const removeInvoiceFromTransaction = useCallback(
        (transactionId, invoicesIds) => reconcileTransactionWithInvoices(transactionId, invoicesIds, { silent: true }),
        [reconcileTransactionWithInvoices],
    );

    return {
        markInvoiceAsReconciled,
        markInvoiceAsUnreconciled,
        saveInvoiceAssociations,
        removeTransactionFromInvoice,

        markTransactionAsReconciled,
        markTransactionAsUnreconciled,
        saveTransactionAssociations,
        removeInvoiceFromTransaction,

        reconcileInvoiceWithTransactions,
        reconcileTransactionWithInvoices,
    };
}
